import React from 'react';
import { CreateFormFields, ModalCreateFileProps } from './types';
import { Button, Checkbox } from '@vtbhome/juicyfront';
import { Body, Footer } from './modal-create-file.styles';
import { Field, Form } from 'react-final-form';
import { CreateFormKeys } from '../../config/form-constants';
import { validate } from '../../lib/form-validate';
import { Typography } from '@shared/ui/typography/typography';
import { InputText } from '@shared/ui/input-text/input-text';
import { InputNumber } from '@shared/ui/input-number/input-number';
import { useFileCreateMutation } from '../../api/create-file-api';
import { CACHE_KEY_FILE_CREATE_PAYMENT } from '../../config/api-constants';
import { InputFile } from '@shared/ui/input-file/input-file';
import { DEFAULT_INPUT_FILE_VALUE } from '@shared/ui/input-file/constants';
import { Select } from '@shared/ui/select/select';
import { DEFAULT_DISK_OPTION, DISK_OPTIONS } from '../../config/ui-constants';
import { Modal } from '@shared/ui/modal/modal';
import { useResetModalForm } from '@shared/lib/hooks/use-reset-modal-form/use-reset-modal-form';

export const ModalCreateFile: React.FC<ModalCreateFileProps> = ({ isShow, onClose, onAccept }) => {
    const formRef = useResetModalForm<CreateFormFields>(isShow);
    const [createFile, createFileResult] = useFileCreateMutation({ fixedCacheKey: CACHE_KEY_FILE_CREATE_PAYMENT });

    const isLoadingForm = createFileResult.isLoading;

    const handleSubmit = async (fields: CreateFormFields) => {
        const result = await createFile({
            disk: fields[CreateFormKeys.Disk][0].value,
            code: fields[CreateFormKeys.Code],
            name: fields[CreateFormKeys.Name],
            path: fields[CreateFormKeys.Path],
            sort: fields[CreateFormKeys.Sort],
            auth: !fields[CreateFormKeys.Auth],
            uploadedFile: fields[CreateFormKeys.UploadedFile].localFiles[0]?.file,
        });
        if (!result.error) {
            onAccept();
        }
    };

    return (
        <Form
            onSubmit={handleSubmit}
            validate={validate}
            render={(form) => {
                formRef.current = form;
                return (
                    <Modal
                        isShow={isShow}
                        variant='drawer'
                        size='l'
                        showLine={false}
                        header={<Typography size='h6'>Добавление файла</Typography>}
                        footer={
                            <Footer>
                                <Button disabled={isLoadingForm} buttonType='light' onClick={onClose}>
                                    Отменить
                                </Button>
                                <Button
                                    preloader={isLoadingForm}
                                    disabled={isLoadingForm}
                                    buttonType='primary'
                                    onClick={form.handleSubmit}>
                                    Подтвердить
                                </Button>
                            </Footer>
                        }
                        onClose={onClose}>
                        <Body>
                            <Field
                                name={CreateFormKeys.UploadedFile}
                                initialValue={DEFAULT_INPUT_FILE_VALUE}
                                render={({ input, meta }) => {
                                    return (
                                        <InputFile
                                            disabled={isLoadingForm}
                                            name={CreateFormKeys.UploadedFile}
                                            label='Файл в формате PDF, DOC, DOCX, XLS, XLSX, JPEG, JPG, PNG'
                                            value={input.value}
                                            error={meta.touched && meta.error}
                                            placeholder='Загрузить файл'
                                            accept='.doc,.docx,.pdf,.xls,.xlsx,.jpeg,.jpg,.png'
                                            count={1}
                                            required
                                            onChange={input.onChange}
                                        />
                                    );
                                }}
                            />
                            <Field
                                name={CreateFormKeys.Name}
                                render={({ input, meta }) => (
                                    <InputText
                                        disabled={isLoadingForm}
                                        required
                                        label='Название файла'
                                        placeholder='Лицензионное соглашение'
                                        value={input.value || ''}
                                        error={meta.touched && meta.error}
                                        onChange={input.onChange}
                                    />
                                )}
                            />
                            <Field
                                name={CreateFormKeys.Code}
                                render={({ input, meta }) => (
                                    <InputText
                                        disabled={isLoadingForm}
                                        required
                                        label='Системное название (код)'
                                        placeholder='license-agreement'
                                        value={input.value || ''}
                                        error={meta.touched && meta.error}
                                        mask={/[^a-z0-9_-]/gi}
                                        onChange={input.onChange}
                                    />
                                )}
                            />
                            <Field
                                name={CreateFormKeys.Path}
                                render={({ input, meta }) => (
                                    <InputText
                                        required
                                        disabled={isLoadingForm}
                                        startValue='/'
                                        label='Относительный путь'
                                        placeholder='/directory'
                                        value={input.value || '/'}
                                        error={meta.touched && meta.error}
                                        mask={/[^a-z0-9/_-]/gi}
                                        onChange={input.onChange}
                                    />
                                )}
                            />
                            <Field
                                name={CreateFormKeys.Disk}
                                initialValue={DEFAULT_DISK_OPTION}
                                render={({ input, meta }) => {
                                    return (
                                        <Select
                                            disabled={isLoadingForm}
                                            required
                                            label='Диск'
                                            placeholder='Выберите диск'
                                            values={input.value}
                                            options={DISK_OPTIONS}
                                            error={meta.touched && meta.error}
                                            onChange={input.onChange}
                                        />
                                    );
                                }}
                            />
                            <Field
                                initialValue={'0'}
                                name={CreateFormKeys.Sort}
                                render={({ input, meta }) => (
                                    <InputNumber
                                        {...input}
                                        disabled={isLoadingForm}
                                        label='Сортировка'
                                        placeholder='Введите число'
                                        hint='Чем меньше число, тем выше файл в списке'
                                        error={meta.touched && meta.error}
                                    />
                                )}
                            />
                            <Field
                                type='checkbox'
                                name={CreateFormKeys.Auth}
                                defaultValue={true}
                                render={({ input }) => (
                                    <Checkbox
                                        label='Получение файла без авторизации'
                                        name={CreateFormKeys.Auth}
                                        checked={input.checked}
                                        disabled={isLoadingForm}
                                        onChange={input.onChange}
                                    />
                                )}
                            />
                        </Body>
                    </Modal>
                );
            }}
        />
    );
};
